import { getDocument } from '@atlaskit/browser-apis';

import { CLI_ROUTE_PATH, HOME_ROUTE_PATH } from '@atlassian/twg-microsite-experience/routes';

// The microsite registers a small, fixed set of routes. Anything else is rewritten
// before the Router subscribes.
const KNOWN_PATHS = new Set<string>([HOME_ROUTE_PATH, CLI_ROUTE_PATH]);

export type InitialLocation = {
	pathname: string;
	search: string;
	hash: string;
};

const stripTrailingSlash = (pathname: string): string =>
	pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

/**
 * The route the delivered HTML document says it is, taken from its
 * `<link rel="canonical">`.
 *
 * Returns `undefined` when the document carries no canonical, or one that is
 * unparseable or names a path this app does not route. Both shipped documents
 * always carry exactly one, which `src/__tests__/html-metadata.test.ts` pins.
 */
export const deliveredRoutePath = (doc: Document | null = getDocument()): string | undefined => {
	const href = doc?.querySelector('link[rel="canonical"]')?.getAttribute('href');
	if (!href) {
		return undefined;
	}

	let pathname: string;
	try {
		pathname = new URL(href, 'https://teamworkgraph.com').pathname;
	} catch {
		return undefined;
	}

	const normalised = stripTrailingSlash(pathname);
	return KNOWN_PATHS.has(normalised) ? normalised : undefined;
};

/**
 * Resolves the location the Router should start at.
 *
 * Resolution happens in two steps, because the URL alone is not enough to know
 * which document was served.
 *
 * A path that is already a known route wins outright, after a single trailing
 * slash is stripped. That covers `/`, `/cli` and `/cli/`, and it means the local
 * dev server does not need to reproduce the gateway's document routing for `/cli`
 * to work.
 *
 * Anything else is resolved from the delivered document's canonical rather than
 * from a normalisation rule invented here. The gateway, not this app, decides
 * which document answers a given URL, and that mapping is not derivable from the
 * client: `/cli/index.html` and `/cli//` may be answered with the CLI document, or
 * may miss and fall through to the `cloudfrontCustomErrorResponses` mapping in
 * `twg-microsite.sd.yaml` and be answered with the home document. Guessing either
 * way renders one route's page underneath the other route's `<head>`, which is the
 * exact defect the per-route documents exist to remove. Reading the canonical
 * means the rendered page always agrees with the metadata that was delivered,
 * whichever document that was.
 *
 * If there is no usable canonical the location falls back to `/`, which also
 * covers a genuinely unknown path such as `/hello` and avoids an extra render
 * pass or a loading flicker on first mount.
 *
 * `search` and `hash` are always preserved, so a rewrite cannot drop a deep-link
 * anchor or campaign parameters.
 */
export const resolveInitialLocation = (
	{ pathname, search, hash }: InitialLocation,
	deliveredPath: string | undefined = deliveredRoutePath(),
): InitialLocation => {
	const normalisedPathname = stripTrailingSlash(pathname);
	if (KNOWN_PATHS.has(normalisedPathname)) {
		return { pathname: normalisedPathname, search, hash };
	}

	return { pathname: deliveredPath ?? HOME_ROUTE_PATH, search, hash };
};
